import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sprout, AtSign } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/dashboard";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(username.trim().toLowerCase(), password);
      nav(from, { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-app grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[var(--cm-ink)] text-[var(--cm-surface)] relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-full bg-[var(--cm-accent)] grid place-items-center">
            <Sprout className="h-4 w-4 text-[var(--cm-ink)]" />
          </span>
          <span className="font-display font-bold text-xl">Bahoi <span className="opacity-70">· causematch</span></span>
        </Link>
        <div>
          <h2 className="font-display font-bold text-4xl leading-tight tracking-tight max-w-md">
            "I gave four hours to the river. I got back ceramics, sourdough, and a Spanish lesson."
          </h2>
          <p className="text-white/60 mt-6">— how Time Hours feel.</p>
        </div>
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[var(--cm-primary)]/30 blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <span className="h-9 w-9 rounded-full bg-[var(--cm-secondary)] grid place-items-center text-white">
              <Sprout className="h-4 w-4" />
            </span>
            <span className="font-display font-bold text-xl">Bahoi <span className="opacity-70">· causematch</span></span>
          </Link>
          <span className="overline text-[var(--cm-primary)]">Welcome back</span>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">Sign in</h1>
          <p className="text-ink-soft mt-2">
            New here?{" "}
            <Link to="/register" className="text-[var(--cm-primary)] underline underline-offset-4 font-semibold" data-testid="login-register-link">
              Create an account.
            </Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <Field label="Username" testid="login-username-field">
              <div className="relative">
                <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  data-testid="login-username-input"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-surface border border-ink-soft rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50 focus:border-[var(--cm-primary)]"
                  placeholder="yourname"
                />
              </div>
            </Field>
            <Field label="Password" testid="login-password-field">
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50 focus:border-[var(--cm-primary)]"
                placeholder="••••••••"
              />
            </Field>
            {err && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
                {err}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
